import * as types from '../mutation-types'
import form from '@/api/form'
import Vue from 'vue'

const state = {
  questions: {},
  conditionalQuestions: {},
  formInfo: null,
  questionnaireForm: {},
  activeFormQuestionIndex: 0,
  submittedFormQuestionIndex: 0,
  getProjectSubmission: false,
  getProjectPaymentInfo: false,
  projectSubmissionModalActive: false,
  questionnaireFormCount: 0,
  activeQuestionnaire: {},
  loading: false,
  paymentFormLoading: false,
  projectFormLoading: false,
  questionnaireLoading: false
}

const getters = {
  questions: (state) => (formId) => {
    return state.questions[formId] ? state.questions[formId] : []
  },
  formInfo: (state) => {
    return state.formInfo ? state.formInfo : null
  },
  questionnaireForm: (state) => (index) => {
    return state.questionnaireForm[index] ? state.questionnaireForm[index] : null
  },
  activeFormQuestionIndex: (state) => {
    return state.activeFormQuestionIndex ? state.activeFormQuestionIndex : 0
  },
  submittedFormQuestionIndex: (state) => {
    return state.submittedFormQuestionIndex ? state.submittedFormQuestionIndex : 0
  },
  isLastPageOfFormSubmitted: (state) => {
    return (state.questionnaireFormCount && state.submittedFormQuestionIndex) ? state.questionnaireFormCount === state.submittedFormQuestionIndex : false
  },
  questionnaireFormCount: (state) => {
    return state.questionnaireFormCount ? state.questionnaireFormCount : 0
  },
  activeQuestionnaire: (state) => (questionnaireId) => {
    return state.activeQuestionnaire[questionnaireId] ? state.activeQuestionnaire[questionnaireId] : []
  },
  getProjectSubmission: (state) => {
    return state.getProjectSubmission
  },
  getProjectPaymentInfo: (state) => {
    return state.getProjectPaymentInfo
  },
  getProjectSubmissionModalActive: (state) => {
    return state.projectSubmissionModalActive
  },
  formsLoading: (state) => {
    return state.loading
  },
  paymentFormLoading: (state) => {
    return state.paymentFormLoading
  },
  projectFormLoading: (state) => {
    return state.projectFormLoading
  },
  questionnaireLoading: (state) => {
    return state.questionnaireLoading
  }
}

const actions = {
  getFormQuestions ({ commit, state }, { formId, includeUser, includeQuestions, includeAnswers, getAllQuestions }) {
    if (state.getProjectSubmission || state.getProjectPaymentInfo) {
      commit(types.SET_FORM_LOADING, true)
    }
    return new Promise((resolve, reject) => {
      form.get(formId, includeUser, includeQuestions, includeAnswers, getAllQuestions).then((response) => {
        const questions = response.data.result.questions
        const form = response.data.result.form
        const conditionalQuestions = response.data.result.conditionalQuestions
        commit(types.ADD_QUESTIONS, { formId: form.id, questions: questions })
        commit(types.SORT_QUESTIONS, form.id)
        commit(types.ADD_FORM_INFO, form)
        if (conditionalQuestions !== null) {
          conditionalQuestions.forEach((conditionalQuestion) => {
            commit(types.SET_DEPENDANT_QUESTIONS, conditionalQuestion)
          })
        }
        resolve()
        commit(types.SET_FORM_LOADING, false)
      }, (error) => {
        reject(error)
        commit(types.SET_FORM_LOADING, false)
      })
    })
  },
  saveUserFormQuestionAnswers ({ commit }, data) {
    return new Promise((resolve, reject) => {
      form.saveUserFormQuestionAnswers(data).then((response) => {
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  getQuestionnaireForm ({ commit, state }, data) {
    commit(types.SET_QUESTIONNAIRE_LOADING, true)
    commit(types.CLEAR_ACTIVEQUESTIONNAIRES)
    return new Promise((resolve, reject) => {
      form.getQuestionnaireForm(data).then((response) => {
        const questionnaireFormList = response.data.result.questionnaireForm
        commit(types.TOTAL_FORM_QUESTIONNAIRE_COUNT, questionnaireFormList.length)
        questionnaireFormList.forEach((questionnaireForm, index) => {
          if (questionnaireForm[index] !== null) {
            commit(types.ADD_QUESTIONNAIRE_INFO, { questionnaireForm, index })
          }
        })
        resolve()
        commit(types.SET_QUESTIONNAIRE_LOADING, false)
      }, (error) => {
        reject(error)
        commit(types.SET_QUESTIONNAIRE_LOADING, false)
      })
    })
  },
  clearQuestionnaire ({ commit }) {
    commit(types.CLEAR_ACTIVEQUESTIONNAIRES)
  },
  setCurrentQuestionnaireFormIndex ({ commit }, index) {
    commit(types.IDENTIFY_FORM_QUESTION_INDEX, index)
  },
  setSubmittedQuestionnaireFormIndex ({ commit }, index) {
    commit(types.IDENTIFY_SUBMITTED_FORM_QUESTION_INDEX, index)
  },
  setGetProjectSubmission ({ commit }, value) {
    commit(types.SET_GET_PROJECT_SUBMISSION, value)
  },
  setGetProjectPaymentSubmission ({ commit }, value) {
    commit(types.SET_GET_PROJECT_PAYMENT_INFO, value)
  },
  setSubmissionModalActive ({ commit }, value) {
    commit(types.SET_SUBMISSION_MODAL_ACTIVE, value)
  },
  setDropDownAnswer ({ commit }, { formId, questionId, answer, checkConditionalQuestion, singleSelect }) {
    return new Promise((resolve, reject) => {
      commit(types.SET_DROPDOWN_ANSWER, { formId, questionId, answer, checkConditionalQuestion, singleSelect })
    })
  },
  setStringAnswer ({ commit }, { formId, questionId, answer }) {
    return new Promise((resolve, reject) => {
      commit(types.SET_STRING_ANSWER, { formId, questionId, answer })
    })
  },
  handleMultiselectConditional ({ commit }, { formId, parentQuestion, answers }) {
    return new Promise((resolve, reject) => {
      commit(types.HANDLE_CONDITIONAL_ON_MULTISELECT, { formId, parentQuestion, answers })
    })
  },
  setPaymentFormLoading ({ commit }, value) {
    commit(types.SET_PAYMENT_FORM_LOADING, value)
  },
  setProjectFormLoading ({ commit }, value) {
    commit(types.SET_PROJECT_FORM_LOADING, value)
  }
}

const mutations = {
  [types.ADD_QUESTIONS] (state, { formId, questions }) {
    Vue.set(state.questions, formId, questions)
  },
  [types.SET_DEPENDANT_QUESTIONS] (state, conditionalQuestion) {
    if (!state.conditionalQuestions[conditionalQuestion.question_id]) {
      Vue.set(state.conditionalQuestions, conditionalQuestion.question_id, {})
    }
    Vue.set(state.conditionalQuestions[conditionalQuestion.question_id], conditionalQuestion.dependent_question_option_id, conditionalQuestion)
  },
  [types.SORT_QUESTIONS] (state, formId) {
    state.questions[formId] = state.questions[formId] ? state.questions[formId].sort((a, b) => a.sortOrder - b.sortOrder) : []
  },
  [types.ADD_FORM_INFO] (state, formInfo) {
    state.formInfo = formInfo
  },
  [types.TOTAL_FORM_QUESTIONNAIRE_COUNT] (state, count) {
    state.questionnaireFormCount = count
  },
  [types.ADD_QUESTIONNAIRE_INFO] (state, params) {
    Vue.set(state.questionnaireForm, params.index, params.questionnaireForm)
    if (!state.activeQuestionnaire[params.questionnaireForm.questionnaire_id]) {
      state.activeQuestionnaire[params.questionnaireForm.questionnaire_id] = []
    }
    state.activeQuestionnaire[params.questionnaireForm.questionnaire_id].push(params.questionnaireForm)
  },
  [types.IDENTIFY_FORM_QUESTION_INDEX] (state, index) {
    state.activeFormQuestionIndex = index
  },
  [types.IDENTIFY_SUBMITTED_FORM_QUESTION_INDEX] (state, index) {
    state.submittedFormQuestionIndex = index
  },
  [types.CLEAR_ACTIVEQUESTIONNAIRES] (state) {
    state.questions = {}
    state.conditionalQuestions = {}
    state.formInfo = null
    state.questionnaireForm = {}
    state.activeFormQuestionIndex = 0
    state.questionnaireFormCount = 0
    state.submittedFormQuestionIndex = 0
    state.activeQuestionnaire = {}
  },
  [types.HANDLE_CONDITIONAL_ON_MULTISELECT] (state, { formId, parentQuestion, answers }) {
    if (Object.keys(state.conditionalQuestions).length > 0) {
      state.questions[formId].forEach(question => {
        if (question.isConditional && !parentQuestion.isConditional && state.conditionalQuestions[question.id]) {
          let matchFound = false
          answers.forEach((answer) => {
            parentQuestion.options.forEach((option) => {
              if (option.label === answer && state.conditionalQuestions[question.id][option.id] &&
                  state.conditionalQuestions[question.id][option.id].dependent_question_id === parentQuestion.id) {
                if (state.conditionalQuestions[question.id][option.id].dependent_question_answer === answer) {
                  matchFound = true
                } else {
                  question.isHidden = true
                }
              }
            })
          })
          if (question.isConditional && matchFound) {
            question.isHidden = false
          }

          if (question.isConditional && (!matchFound || answers.length === 0)) {
            question.isHidden = true
          }
        }
      })
    }
  },
  [types.SET_DROPDOWN_ANSWER] (state, { formId, questionId, answer, checkConditionalQuestion }) {
    let parentQuestion = null
    state.questions[formId].forEach(question => {
      if (question.id === questionId) {
        question.dropdownAnswer = answer
        parentQuestion = question
      }
    })

    if (checkConditionalQuestion && Object.keys(state.conditionalQuestions).length > 0 && !parentQuestion.isConditional) {
      state.questions[formId].forEach(question => {
        let matchFound = false
        if (question.isConditional) {
          if (state.conditionalQuestions[question.id] &&
              state.conditionalQuestions[question.id][answer.id] &&
              state.conditionalQuestions[question.id][answer.id].dependent_question_id === questionId) {
            matchFound = true
          }
          if (!matchFound) {
            question.isHidden = true
          } else {
            question.isHidden = false
          }
        }
      })
    }
  },
  [types.SET_STRING_ANSWER] (state, { formId, questionId, answer }) {
    state.questions[formId].forEach(question => {
      if (question.id === questionId) {
        question.userStringAnswer = answer
      }
    })
  },
  [types.RESET_ON_LOGOUT] (state) {
    state.questions = {}
    state.formInfo = null
    state.questionnaireForm = {}
    state.activeFormQuestionIndex = 0
    state.questionnaireFormCount = 0
    state.submittedFormQuestionIndex = 0
    state.activeQuestionnaire = {}
  },
  [types.SET_GET_PROJECT_SUBMISSION] (state, value) {
    state.getProjectSubmission = value
  },
  [types.SET_GET_PROJECT_PAYMENT_INFO] (state, value) {
    state.getProjectPaymentInfo = value
  },
  [types.SET_SUBMISSION_MODAL_ACTIVE] (state, value) {
    state.projectSubmissionModalActive = value
  },
  [types.SET_FORM_LOADING] (state, loading) {
    state.loading = loading
  },
  [types.SET_PAYMENT_FORM_LOADING] (state, loading) {
    state.paymentFormLoading = loading
  },
  [types.SET_PROJECT_FORM_LOADING] (state, loading) {
    state.projectFormLoading = loading
  },
  [types.SET_QUESTIONNAIRE_LOADING] (state, loading) {
    state.questionnaireLoading = loading
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
